import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { toast } from '../../toasts';
import { useLocation, useNavigate } from 'react-router-dom';
import ContractService from '../../services/contract.service';
import { getError, showCurrency } from '../../utils';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { Section, StatStrip, DataTable, IdChip, Pill, RowActionsMenu, Button } from '../../components/EntityList';
import { useLabels } from '../../hooks/useLabels';

function formatDate(date) {
	return date ? moment.utc(date).format('DD/MM/YYYY') : '';
}

function formatMoney(currency, amount) {
	return `${showCurrency(currency) || ''} ${Number(amount || 0).toLocaleString('es-UY')}`.trim();
}

function isVigente(contract) {
	return contract.status !== 'closed' && !contract.closeDate;
}

function AccountContracts({ accountId, rows, loading, error, onRefetch }) {
	const { t } = useLabels();
	const navigate = useNavigate();
	const location = useLocation();

	// Sends the editor back to this exact tab on save/cancel instead of the
	// account's default tab (or the new contract's detail page).
	const returnTo = () => encodeURIComponent(location.pathname + location.search);
	const [deletingContract, setDeletingContract] = useState(null);

	const editContractHandler = (contract) => {
		navigate(`/AdminScreen/accounts/account/${accountId}/contracts/${contract._id}/edit`);
	};

	const stats = useMemo(() => {
		const vigentes = rows.filter(isVigente);
		const rentaByCurrency = new Map();
		vigentes.forEach((contract) => {
			const key = contract.currency || '—';
			rentaByCurrency.set(key, (rentaByCurrency.get(key) || 0) + (contract.amount || 0));
		});
		return [
			{ label: 'Vigentes', value: vigentes.length },
			{
				label: 'Renta mensual',
				value: [...rentaByCurrency.entries()].map(([currency, amount]) => formatMoney(currency, amount)).join(' · ') || '—',
				mono: true,
			},
		];
	}, [rows]);

	async function handleConfirmDelete() {
		try {
			await ContractService.delete(deletingContract._id);
			toast.success('Contrato Eliminado');
			onRefetch();
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setDeletingContract(null);
		}
	}

	const columns = [
		{ key: 'id', header: 'ID', render: (contract) => <IdChip>{contract.customId}</IdChip> },
		// The account can appear on a contract as tenant, as owner of the rented
		// asset, or both — the parent tags each row so the two sources stay
		// distinguishable once they're merged into one list.
		{
			key: 'accountRole',
			header: 'Rol',
			render: (contract) => (contract.accountRole ? <Pill tone='neutral'>{contract.accountRole}</Pill> : null),
		},
		{ key: 'property', header: 'Inmueble', render: (contract) => contract.property?.name },
		{ key: 'owner', header: 'Propietario', render: (contract) => contract.property?.owner?.name },
		{ key: 'periodInit', header: 'Inicio', render: (contract) => formatDate(contract.periodInit) },
		{ key: 'periodEnd', header: 'Fin', render: (contract) => formatDate(contract.periodEnd) },
		{
			key: 'amount',
			header: 'Renta',
			className: 'tw:text-right',
			render: (contract) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(contract.currency, contract.amount)}</span>,
		},
		{
			key: 'estado',
			header: 'Estado',
			render: (contract) => <Pill tone={isVigente(contract) ? 'accent' : 'neutral'}>{isVigente(contract) ? 'Vigente' : 'Finalizado'}</Pill>,
		},
	];

	const renderRowActions = (contract) => (
		<RowActionsMenu
			items={[
				{ label: 'Editar', onClick: () => editContractHandler(contract) },
				{ label: 'Eliminar', onClick: () => setDeletingContract(contract), danger: true },
			]}
		/>
	);

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			<StatStrip stats={stats} />
			<div className='tw:flex tw:justify-end'>
				<Button size='sm' variant='primary' onClick={() => navigate(`/AdminScreen/accounts/account/${accountId}/newContract?returnTo=${returnTo()}`)}>
					Nuevo alquiler
				</Button>
			</div>
			<Section title='Alquileres'>
				<DataTable
					columns={columns}
					rows={rows}
					loading={loading}
					error={error}
					onRowClick={(contract) => navigate(`/AdminScreen/contracts/contract/${contract._id}`)}
					rowActions={renderRowActions}
					emptyMessage='No se encontraron alquileres para esta cuenta.'
				/>
			</Section>

			<ConfirmationModal
				show={Boolean(deletingContract)}
				handleClose={() => setDeletingContract(null)}
				handleConfirm={handleConfirmDelete}
				title={t('title.deleteContract')}
				description={t('msg.confirmDeleteContract')}
				confirmCaption={t('action.delete')}
				cancelCaption={t('action.cancel')}
			/>
		</div>
	);
}

export default AccountContracts;
